import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, useLocation, useNavigate } from 'react-router-dom';

const getPathFromHash = (hash: string): string | null => {
  if (!hash || hash.length <= 1) {
    return null;
  }

  const valueWithoutHash = hash.substring(1).trim();

  // Mantem hash de callback OAuth (ex: access_token=..., id_token=..., etc.)
  if (!valueWithoutHash.startsWith('/')) {
    return null;
  }

  return valueWithoutHash;
};

/**
 * Processa o hash da URL antes do React Router ser inicializado.
 * Converte URLs com hash para URLs sem hash.
 * 
 * Exemplo: /#/home -> /home
 */
export const processHashBeforeRouter = (): void => {
  const pathWithoutHash = getPathFromHash(window.location.hash);

  if (pathWithoutHash && pathWithoutHash !== window.location.pathname) {
    // Remove o hash da URL e redireciona para o path sem hash
    window.history.replaceState(null, '', pathWithoutHash);
  }
};

/**
 * Componente de compatibilidade que monitora e converte URLs com hash para sem hash.
 * Garante que se alguém navegar usando hash (ex: via link externo), será convertido.
 */
const HashCompatibility: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathWithoutHash = getPathFromHash(window.location.hash);

    // Se a URL tem hash de rota (ex: /#/home), converte para sem hash (ex: /home)
    if (pathWithoutHash && pathWithoutHash !== location.pathname) {
      // Remove o hash da URL
      window.history.replaceState(null, '', pathWithoutHash);
      navigate(pathWithoutHash, { replace: true });
    } else if (pathWithoutHash && pathWithoutHash === location.pathname) {
      // Se o path já está correto mas ainda tem hash na URL, remove apenas o hash
      window.history.replaceState(null, '', pathWithoutHash);
    }
  }, [location.pathname, navigate]);

  return <>{children}</>;
};

interface CompatibleRouterProps {
  children: React.ReactNode;
  /**
   * Se true, força o uso de HashRouter mesmo que não haja hash na URL
   * Se false (padrão), usa BrowserRouter (sem hash)
   */
  forceHashMode?: boolean;
}

/**
 * Router compatível que suporta tanto rotas com hash quanto sem hash.
 * 
 * Por padrão, usa BrowserRouter (sem hash).
 * Se detectar hash na URL inicial, converte automaticamente para sem hash.
 * 
 * Exemplo:
 * - URL com hash: /#/home → redireciona para /home
 * - URL sem hash: /home → funciona normalmente
 */
export const CompatibleRouter: React.FC<CompatibleRouterProps> = ({ 
  children, 
  forceHashMode = false 
}) => {
  // Se forceHashMode for true, usa HashRouter
  if (forceHashMode) {
    return <HashRouter>{children}</HashRouter>;
  }

  // Por padrão, usa BrowserRouter com compatibilidade para hash
  return (
    <BrowserRouter>
      <HashCompatibility>
        {children}
      </HashCompatibility>
    </BrowserRouter>
  );
};

export default CompatibleRouter;

